import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Building2, Award, FileText, AlertCircle } from "lucide-react";
import Seal from "@/components/Seal";

const ACIS_STANDARDS = [
  "Institutional Governance and Administrative Responsibility",
  "Faculty Qualifications and Instructional Competency",
  "Curriculum Quality and Program Design",
  "Learning Outcomes and Student Achievement",
  "Student Records and Documentation Standards",
  "Assessment, Evaluation, and Competency Verification",
  "Ethics, Compliance, and Scope of Practice Alignment",
  "Operational Sustainability and Institutional Integrity",
];

const IBTHM_STANDARDS = [
  "Practitioner Education and Training Standards",
  "Board Certification Eligibility Standards",
  "Professional Licensure Eligibility Standards",
  "Continuing Education and Renewal Standards",
  "Ethics and Professional Conduct Standards",
  "Scope of Practice and Referral Standards",
  "Registry Visibility and Public Verification Standards",
  "Disciplinary, Suspension, Revocation, and Reinstatement Standards",
];

export default function Standards() {
  return (
    <div className="bg-white" data-testid="standards-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-primary to-[#0f2b48] text-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-white/70 mb-3">
            <ShieldCheck className="h-3.5 w-3.5" /> Standards Library
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-3">Standards and Requirements</h1>
          <p className="text-sm sm:text-base text-white/80 max-w-3xl leading-relaxed">
            Public titles for the ACIS Accreditation and IBTHM Credentialing standards.
            The full standards manuals are delivered during the application or acceptance
            process — review, signature, and acknowledgement of the complete document are
            a condition of accreditation, credentialing, licensure, or membership.
          </p>
        </div>
      </section>

      {/* Distribution disclosure */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <Card className="p-5 border-amber-200 bg-amber-50/50">
          <div className="flex items-start gap-3">
            <FileText className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-slate-800 leading-relaxed" data-testid="std-distribution-note">
              <strong>Document distribution.</strong> Applicants and members receive the full
              Standards and Requirements document during the application or acceptance process.
              The full standards document must be reviewed, signed, and acknowledged as a
              condition of accreditation, credentialing, licensure, or membership.
            </p>
          </div>
        </Card>
      </section>

      {/* ACIS standards */}
      <section className="max-w-6xl mx-auto px-6 pb-10">
        <Card className="p-6 sm:p-8 border-slate-200" data-testid="std-acis-card">
          <div className="flex items-center gap-4 mb-5">
            <Seal kind="acis_accredited_member" size="md" />
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">ACIS</div>
              <h2 className="font-heading text-2xl font-bold text-primary flex items-center gap-2">
                <Building2 className="h-5 w-5" /> Accreditation Standards
              </h2>
              <p className="text-xs text-slate-600 mt-1">
                Accreditation Commission for Integrative Sciences — Institutional &amp; Programmatic accreditation.
              </p>
            </div>
          </div>
          <ol className="space-y-2">
            {ACIS_STANDARDS.map((s, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`std-acis-${i + 1}`}>
                <Badge variant="outline" className="bg-slate-100 text-slate-700 font-mono-data text-[10px] mt-0.5">
                  {String(i + 1).padStart(2, "0")}
                </Badge>
                <span className="text-sm text-slate-800">{s}</span>
              </li>
            ))}
          </ol>
        </Card>
      </section>

      {/* IBTHM standards */}
      <section className="max-w-6xl mx-auto px-6 pb-12">
        <Card className="p-6 sm:p-8 border-slate-200" data-testid="std-ibthm-card">
          <div className="flex items-center gap-4 mb-5">
            <div className="flex items-center gap-1">
              <Seal kind="ibthm_board_certified" size="md" />
              <Seal kind="ibthm_licensed" size="md" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-500">IBTHM</div>
              <h2 className="font-heading text-2xl font-bold text-primary flex items-center gap-2">
                <Award className="h-5 w-5" /> Credentialing &amp; Licensure Standards
              </h2>
              <p className="text-xs text-slate-600 mt-1">
                International Board of Trichology &amp; Holistic Medicine — Board Certification &amp; Licensure.
              </p>
            </div>
          </div>
          <ol className="space-y-2">
            {IBTHM_STANDARDS.map((s, i) => (
              <li key={i} className="flex items-start gap-3" data-testid={`std-ibthm-${i + 1}`}>
                <Badge variant="outline" className="bg-slate-100 text-slate-700 font-mono-data text-[10px] mt-0.5">
                  {String(i + 1).padStart(2, "0")}
                </Badge>
                <span className="text-sm text-slate-800">{s}</span>
              </li>
            ))}
          </ol>
        </Card>
      </section>

      {/* Legal disclaimer */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <Card className="p-5 border-slate-200 bg-slate-50">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-slate-700 leading-relaxed" data-testid="std-disclaimer">
              <strong>Important.</strong> ACIS accreditation and IBTHM credentials are private
              professional recognition standards and do not replace any state, federal, or
              jurisdictional license required by law. Practitioners and institutions are
              responsible for complying with applicable laws and scope-of-practice requirements.
            </p>
          </div>
        </Card>
      </section>
    </div>
  );
}
